import { useEffect, useState } from 'react';
import { Heading, Search } from '@alefeducation/ui-elements-library-core';
import styled from 'styled-components';
import { SearchOption } from '@alefeducation/ui-elements-library-core/lib/components/Search/Search';
import useContentRepositoriesAPI from './hooks/useContentRepositoriesAPI';
import { ContentRepository } from '../../../../apollo/queries/organisations/contentRepositores.queries';
import { OrganisationSettingVM } from '../../../../apollo/queries/organisations/organisationSetting.queries';
import { InfoTooltip } from '../../../Common';
import MessagePopup from '../../../MessagePopup/MessagePopup';
import { WARNING_MESSAGE, WARNING_TITLE } from '../../../../utils/constants';

const StyledContainer = styled.div`
  margin-top: 16px;
  display: flex;
  align-items: center;
  column-gap: 14px;

  #search {
    min-width: 300px;
    max-width: 420px;
  }
`;

interface Props {
  setting: OrganisationSettingVM;
  onUpdate: (setting: OrganisationSettingVM) => void;
}

const ContentRepositorySearch = (props: Props) => {
  const {
    setting: { name: title, value, settingCode, description },
    setting,
    onUpdate,
  } = props;
  const { contentRepositories } = useContentRepositoriesAPI();
  const [showContentDeletedPopup, setShowContentDeletedPopup] =
    useState<boolean>(false);
  const [deletedSetting, setDeletedSetting] = useState<string>();
  const [updatedSettings, setUpdatedSettings] = useState<
    ContentRepository[] | null
  >();
  const [contentRepositoryOptions, setContentRepositoryOptions] = useState<
    ContentRepository[]
  >([]);

  const [searchTerm, setSearchTerm] = useState<string>('');

  useEffect(() => {
    if (contentRepositories.length > 0) {
      setContentRepositoryOptions(contentRepositories);
    }
  }, [contentRepositories]);

  const getOptions = (cs: ContentRepository[]) =>
    cs.map((cs) => ({ id: cs.id, key: cs.id, value: cs.name }));

  const onOptionsChange = (options: SearchOption[]) => {
    const [deletedContents] = findDeletedContents(props.setting.value, options);
    const updatedSettings = options.map(({ key, value }) => ({
      id: key,
      name: value,
    })) as ContentRepository[];
    if (deletedContents) {
      setDeletedSetting(deletedContents.name);
      setShowContentDeletedPopup(true);
      setUpdatedSettings(updatedSettings);
    } else {
      onUpdate({
        ...setting,
        value: updatedSettings,
      });
    }
    resetSearch();
  };

  const onDeleteContentRepos = () => {
    if (updatedSettings) {
      onUpdate({
        ...setting,
        value: updatedSettings,
      });
      setShowContentDeletedPopup(false);
      setUpdatedSettings(null);
    }
  };

  const findDeletedContents = (oldSettings: any, newSettings: any) =>
    oldSettings.filter(
      (old: any) => !newSettings.some((updated: any) => old.id === updated.key),
    );

  const getWarningMessage = (name: string = '') => {
    return `The ${name} ${WARNING_MESSAGE}`;
  };

  const onInputChange = (value: string) => {
    setSearchTerm(value);
  };

  const resetSearch = () => {
    setSearchTerm('');
    setContentRepositoryOptions(contentRepositories);
  };

  useEffect(() => {
    if (searchTerm) {
      const filteredContentRepositories = contentRepositories.filter((cp) =>
        cp.name.toLowerCase().includes(searchTerm.toLowerCase()),
      );
      setContentRepositoryOptions(filteredContentRepositories);
    }
  }, [searchTerm]);
  return (
    <StyledContainer>
      {showContentDeletedPopup && (
        <MessagePopup
          open={showContentDeletedPopup}
          title={WARNING_TITLE}
          message={getWarningMessage(deletedSetting)}
          name={deletedSetting || ''}
          variant="warning"
          buttonText="Remove"
          onAction={() => onDeleteContentRepos()}
          onClose={() => setShowContentDeletedPopup(false)}
        />
      )}
      <Heading variant="heading-small" weight="light">
        {title}
      </Heading>
      <Search
        showToggleIcon
        onInputChangeDebounce={100}
        onChange={onOptionsChange}
        onInputChange={onInputChange}
        options={getOptions(contentRepositoryOptions)}
        selectedOptions={getOptions(value as ContentRepository[])}
        deleteOptionsOnBackspace={false}
      />
      <InfoTooltip id={settingCode} text={description} />
    </StyledContainer>
  );
};

export default ContentRepositorySearch;
